import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Tooltip } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import DynamicPerformanceModal from 'components/performance/DynamicPerformanceModal';
import performanceSlice from 'store/modules/performance';
import exerciseSlice from 'store/modules/exercise';
import requestStatus from 'common/constants/status';
import { membershipStatus } from 'common/constants/options';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';

/**
 * Botón reutilizable "Crear rendimiento" para el panel de un cliente. Abre el modal de creación
 * con el cliente ya fijado y bloqueado, sin sacar al entrenador de la pantalla actual. Si el
 * cliente está inactivo, el botón se deshabilita y un tooltip explica por qué. Al crear, refresca
 * el listado de rendimientos por ejercicio del cliente.
 */
function CreatePerformanceButton({ client = null, onCreated = null, fullWidth = false }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['performances']);
  const [open, setOpen] = useState(false);
  const submitting = useRef(false);

  const exercises = useSelector((state) => state.exercise?.data?.exercises || []);
  const createLoading = useSelector(
    (state) => state.performance.status.createDynamic === requestStatus.loading
  );
  const createSuccess = useSelector(
    (state) => state.performance.status.createDynamic === requestStatus.success
  );

  const isInactive = client?.membership_status === membershipStatus.inactive;

  // Los ejercicios alimentan el selector del modal; se cargan al abrir si aún no están en el store.
  useEffect(() => {
    if (open && exercises.length === 0) {
      dispatch(exerciseSlice.actions.list());
    }
  }, [open, exercises.length, dispatch]);

  // Cierra el modal y refresca cuando ESTA instancia completó una creación.
  useEffect(() => {
    if (createSuccess && submitting.current) {
      submitting.current = false;
      setOpen(false);
      if (client?.id) dispatch(performanceSlice.actions.getDynamicByExercise(client.id));
      if (onCreated) onCreated();
    }
  }, [createSuccess, client, dispatch, onCreated]);

  const handleSubmit = (data) => {
    submitting.current = true;
    dispatch(performanceSlice.actions.createDynamic(data));
  };

  const button = (
    <Button
      onClick={() => setOpen(true)}
      disabled={isInactive}
      startIcon={<AddIcon />}
      fullWidth={fullWidth}
      sx={{ ...premiumButtonSx, py: 0.9, px: 2.5, fontSize: '0.9rem' }}
    >
      {t('dynamicPerformances.newPerformance', { ns: 'performances', defaultValue: 'Crear rendimiento' })}
    </Button>
  );

  return (
    <>
      {isInactive ? (
        // El span permite que el Tooltip funcione aunque el botón esté deshabilitado.
        <Tooltip
          title={t('dynamicPerformances.inactiveClientTooltip', {
            ns: 'performances',
            defaultValue: 'El cliente está inactivo. Reactiva su membresía para poder registrar rendimientos.'
          })}
        >
          <Box component="span" sx={{ display: fullWidth ? 'block' : 'inline-flex' }}>{button}</Box>
        </Tooltip>
      ) : (
        button
      )}
      <DynamicPerformanceModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        clients={client ? [client] : []}
        lockedClient={client}
        exercises={exercises}
        loading={createLoading}
      />
    </>
  );
}

CreatePerformanceButton.propTypes = {
  client: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    membership_status: PropTypes.string
  }),
  onCreated: PropTypes.func,
  fullWidth: PropTypes.bool
};

export default CreatePerformanceButton;
